import React, {useState, useMemo} from "react";
import {
    ArrowLeft,
    User,
    Mail,
    Calendar,
    Loader,
    Receipt,
    Clock,
    CreditCard,
    PieChart,
    BarChart3,
    Trophy,
    Award,
    FileText
} from "lucide-react";
import {useQuery} from '@tanstack/react-query';
import Chart from "react-google-charts";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import moment from "moment";
import PrimeModal from "./components/PrimeModal";
import Button from "../Common/Button";
import EmployeeSoldProducts from "./components/EmployeeSoldProducts";
import {ListEmpty, Pagination, SummaryStrip, mobileListClass, mobileRowClass, thClass} from "../../stock/StockUI";

// Moment's locale is synced globally by useSyncMomentLocale() at the app root.

// Pure formatters hoisted to module scope so they aren't recreated on every render.
const formatCurrency = (value) => {
    if (value === null || value === undefined) return "0.00";
    return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const formatDate = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString, "YYYY-MM-DD HH:mm:ss").format('DD/MM/YYYY');
};

const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return moment(dateString, "YYYY-MM-DD HH:mm:ss").format('DD/MM/YYYY HH:mm');
};

const toNumber = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

// Edge-to-edge block with an uppercase title bar (same as the Paramètres /
// Marketplace panes); neighbours are split by hairlines, never by gaps.
const Section = ({title, icon: Icon, aside, children, className = ""}) => (
    <section className={`min-w-0 ${className}`}>
        <div className="min-h-[3rem] flex items-center justify-between gap-3 px-4 sm:px-6 py-2 border-b border-gray-200 dark:border-gray-700">
            <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                {Icon && <Icon className="w-4 h-4 text-primary"/>}{title}
            </h2>
            {aside}
        </div>
        {children}
    </section>
);

const EmptyChart = ({height, text}) => (
    <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400" style={{minHeight: `${height}px`}}>
        {text}
    </div>
);

const EmployeeDetails = ({employeeId, employeeName, onBack, dateRange, startTime, endTime, isDarkMode = false}) => {
    const {t} = useTranslation("activite");
    const [currentPage, setCurrentPage] = useState(1);
    const [showPrimeModal, setShowPrimeModal] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    const fetchEmployeeDetails = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build query parameters
            let url = `/activite/employee/${employeeId}/details`;
            const params = [];

            if (dateRange && dateRange.length > 0) {
                const range = dateRange[0];
                if (range.startDate) {
                    params.push(`startDate=${encodeURIComponent(moment(range.startDate).format('YYYY-MM-DD'))}`);
                }
                if (range.endDate) {
                    params.push(`endDate=${encodeURIComponent(moment(range.endDate).format('YYYY-MM-DD'))}`);
                }
            }

            if (startTime) {
                params.push(`startTime=${encodeURIComponent(startTime)}`);
            }

            if (endTime) {
                params.push(`endTime=${encodeURIComponent(endTime)}`);
            }

            params.push(`page=${currentPage}`);

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                t("employeeDetails.fetchError");
            throw new Error(errorMessage);
        }
    };

    // ── PDF report generation ───────────────────────────────────────────────────
    const generateEmployeeReport = async () => {
        setIsGeneratingReport(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            if (!accountId?.id) {
                toast.error(t("employeeDetails.accountNotSelected"));
                return;
            }

            const params = [];
            if (dateRange && dateRange.length > 0) {
                const range = dateRange[0];
                if (range.startDate) {
                    params.push(`startDate=${encodeURIComponent(moment(range.startDate).format('YYYY-MM-DD'))}`);
                }
                if (range.endDate) {
                    params.push(`endDate=${encodeURIComponent(moment(range.endDate).format('YYYY-MM-DD'))}`);
                }
            }
            const query = params.length > 0 ? `?${params.join('&')}` : '';

            const res = await api.get(`/generate_employee_report/${employeeId}${query}`, {
                headers: {
                    "X-account-id": accountId.id,
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                }
            });

            if (res.data?.status === "OK" && res.data?.url) {
                window.open(res.data.url, '_blank');
                toast.success(t("employeeDetails.reportGenerated", {number: res.data.report_number}));
            } else {
                toast.error(t("employeeDetails.reportGenerationError"));
            }
        } catch (e) {
            console.error("Error generating employee report:", e);
            toast.error(e.response?.data?.error || e.response?.data?.message || t("employeeDetails.reportGenerationError"));
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const {
        data: employeeDetails,
        isLoading,
        error
    } = useQuery({
        queryKey: ['employee-details', employeeId, currentPage, dateRange, startTime, endTime],
        queryFn: fetchEmployeeDetails,
        keepPreviousData: true
    });

    const statistics = employeeDetails?.statistics;

    // Period-wide derived indicators — computed from aggregate statistics (not the
    // paginated sales_history) so they stay accurate across the whole period.
    const derived = useMemo(() => {
        if (!statistics) return null;

        const revenueTTC = toNumber(statistics.total_revenue);
        const revenueHT = toNumber(statistics.total_revenue_ht);
        const sales = toNumber(statistics.total_sales);
        const products = toNumber(statistics.total_products_sold);
        const discounts = toNumber(statistics.total_discounts_applied);

        const vat = Math.max(0, revenueTTC - revenueHT);
        const gross = revenueTTC + discounts;

        return {
            vat,
            itemsPerSale: sales > 0 ? products / sales : 0,
            discountRate: gross > 0 ? (discounts / gross) * 100 : 0,
            revenueHT,
        };
    }, [statistics]);

    // Donut data for the HT / VAT revenue split.
    const revenueSplit = useMemo(() => {
        if (!derived || (derived.revenueHT <= 0 && derived.vat <= 0)) return null;
        return [
            [t("employeeDetails.chart.type"), t("employeeDetails.chart.amount")],
            [t("employeeDetails.chart.excludingTax"), derived.revenueHT],
            [t("employeeDetails.chart.vat"), derived.vat],
        ];
    }, [derived, t]);

    const chartOptions = useMemo(() => ({
        legend: {position: "bottom", textStyle: {color: isDarkMode ? '#9CA3AF' : '#6B7280', fontSize: 12}},
        pieHole: 0.5,
        chartArea: {width: "92%", height: "82%"},
        colors: ["#4DB6AC", "#F59E0B"],
        backgroundColor: {fill: "transparent"},
        pieSliceTextStyle: {color: "#ffffff", fontSize: 12},
        tooltip: {textStyle: {fontSize: 12}},
    }), [isDarkMode]);

    // Hourly performance bar chart — the API (`hourly_distribution`) only returns hours
    // that had at least one sale, so fill in the 24h range with zeros for a real distribution.
    const hourlyDistribution = employeeDetails?.hourly_distribution;
    const hourlyChartData = useMemo(() => {
        if (!hourlyDistribution || hourlyDistribution.length === 0) return null;
        const byHour = new Map(hourlyDistribution.map(h => [h.hour, h]));
        const hasActivity = hourlyDistribution.some(h => toNumber(h.sales_count) > 0);
        if (!hasActivity) return null;
        return [
            [t("employeeDetails.chart.hour"), t("employeeDetails.chart.sales")],
            ...Array.from({length: 24}, (_, h) => [`${h}h`, toNumber(byHour.get(h)?.sales_count)]),
        ];
    }, [hourlyDistribution, t]);

    const hourlyChartOptions = useMemo(() => ({
        legend: {position: "none"},
        chartArea: {width: "94%", height: "78%"},
        colors: ["#4DB6AC"],
        backgroundColor: {fill: "transparent"},
        hAxis: {textStyle: {color: isDarkMode ? '#9CA3AF' : '#6B7280', fontSize: 10}},
        vAxis: {textStyle: {color: isDarkMode ? '#9CA3AF' : '#6B7280', fontSize: 11}, minValue: 0, gridlines: {color: isDarkMode ? '#374151' : '#E5E7EB'}},
        tooltip: {textStyle: {fontSize: 12}},
    }), [isDarkMode]);

    const topProducts = employeeDetails?.top_products;
    const topProductsMax = useMemo(() => {
        if (!topProducts || topProducts.length === 0) return 0;
        return Math.max(...topProducts.map(p => toNumber(p.quantity_sold)));
    }, [topProducts]);

    const salesByStatus = employeeDetails?.sales_by_status;

    const SALE_STATUS_LABELS = {
        terminer: {label: t("employeeDetails.status.completed"), dot: "bg-green-500"},
        en_attente: {label: t("employeeDetails.status.pending"), dot: "bg-amber-500"},
        annulee: {label: t("employeeDetails.status.cancelled"), dot: "bg-red-500"},
        remboursee: {label: t("employeeDetails.status.refunded"), dot: "bg-orange-500"},
    };
    const saleStatusMeta = (status) =>
        SALE_STATUS_LABELS[status] || {label: status ? status.charAt(0).toUpperCase() + status.slice(1) : t("employeeDetails.status.unknown"), dot: "bg-gray-400"};

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-96">
                <Loader className="w-8 h-8 text-primary animate-spin"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-center text-red-500">
                <p className="mb-4">{t("employeeDetails.errorPrefix", {message: error.message})}</p>
                <Button variant="primary" onClick={onBack}>{t("employeeDetails.back")}</Button>
            </div>
        );
    }

    if (!employeeDetails || !employeeDetails.employee) {
        return (
            <div className="p-4 sm:p-6">
                <button
                    onClick={onBack}
                    className={`flex items-center space-x-2 mb-6 hover:text-primary transition-colors ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
                >
                    <ArrowLeft className="w-5 h-5"/>
                    <span className="font-medium">{t("employeeDetails.back")}</span>
                </button>
                <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <User className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}/>
                    <p className="text-sm sm:text-base">{t("employeeDetails.noEmployeeData")}</p>
                </div>
            </div>
        );
    }

    const {employee, period, sales_history, pagination} = employeeDetails;
    const saleCountLabel = (sale) => (sale.products || []).reduce((sum, p) => sum + (p.quantity || 1), 0);
    const paymentLabel = (sale) => (sale.payments || []).map(p => p.name).join(', ') || t("employeeDetails.notAvailable");

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
            {/* Barre du haut : Retour + actions (boutons « plain » des en-têtes) */}
            <header className="flex items-center justify-between gap-2 px-2 sm:px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack}>
                    {t("employeeDetails.back")}
                </Button>
                <div className="flex items-center gap-1 sm:gap-2">
                    <Button variant="plain" icon={FileText} compact loading={isGeneratingReport} onClick={generateEmployeeReport}>
                        {isGeneratingReport ? t("employeeDetails.generating") : t("employeeDetails.pdfReport")}
                    </Button>
                    {statistics && (
                        <Button variant="plain" icon={Award} compact onClick={() => setShowPrimeModal(true)}>
                            {t("employeeDetails.calculateBonus")}
                        </Button>
                    )}
                </div>
            </header>

            {/* Identité + période analysée */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-8 py-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4 min-w-0">
                    <div className="relative flex-shrink-0">
                        <div className="w-14 h-14 rounded-full bg-primary/15 dark:bg-primary/25 text-primary-dark dark:text-primary flex items-center justify-center text-2xl font-semibold">
                            {employee.name.charAt(0).toUpperCase()}
                        </div>
                        <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full ring-2 ring-white dark:ring-gray-900 ${employee.active ? 'bg-green-500' : 'bg-gray-300'}`}/>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                            <h1 className="text-xl font-semibold tracking-tight truncate capitalize">{employee.name}</h1>
                            <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                employee.active
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${employee.active ? 'bg-green-500' : 'bg-gray-400'}`}/>
                                {employee.active ? t("employeeDetails.active") : t("employeeDetails.inactive")}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500 dark:text-gray-400">
                            <span className="inline-flex items-center gap-1.5 capitalize"><User className="w-4 h-4"/>{employee.profil?.label || t("employeeDetails.notAvailable")}</span>
                            <span className="inline-flex items-center gap-1.5 min-w-0" title={employee.email || undefined}>
                                <Mail className="w-4 h-4 flex-shrink-0"/><span className="truncate">{employee.email || t("employeeDetails.notAvailable")}</span>
                            </span>
                            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4"/>{t("employeeDetails.creationDate")} {formatDate(employee.created_at)}</span>
                        </div>
                    </div>
                </div>
                {period && (
                    <div className="inline-flex items-center gap-2 self-start sm:self-auto px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-300">
                        <Clock className="w-4 h-4 text-primary flex-shrink-0"/>
                        <span>{t("employeeDetails.periodAnalyzed")} <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {moment(period.start_date).format('DD/MM/YYYY')} – {moment(period.end_date).format('DD/MM/YYYY')}
                        </span></span>
                    </div>
                )}
            </div>

            {/* Chiffres : mêmes bandes à filets que le Stock */}
            {statistics && (
                <SummaryStrip items={[
                    {key: "sales", label: t("employeeDetails.stats.totalSales"), value: statistics.total_sales},
                    {
                        key: "revenue", label: t("employeeDetails.stats.revenue"), value: `${formatCurrency(statistics.total_revenue)} DH`,
                        hint: t("employeeDetails.stats.htSub", {amount: formatCurrency(statistics.total_revenue_ht)}),
                        color: "text-primary-dark dark:text-primary",
                    },
                    {key: "average", label: t("employeeDetails.stats.averageOrder"), value: `${formatCurrency(statistics.average_order_value)} DH`},
                    {key: "products", label: t("employeeDetails.stats.productsSold"), value: statistics.total_products_sold},
                ]}/>
            )}
            {statistics && derived && (
                <SummaryStrip items={[
                    {key: "discounts", label: t("employeeDetails.stats.discountsApplied"), value: `${formatCurrency(statistics.total_discounts_applied)} DH`, color: "text-red-600 dark:text-red-400"},
                    {key: "discountRate", label: t("employeeDetails.discountRate"), value: `${derived.discountRate.toFixed(1)} %`},
                    {key: "vat", label: t("employeeDetails.vatCollected"), value: `${formatCurrency(derived.vat)} DH`},
                    {key: "itemsPerSale", label: t("employeeDetails.itemsPerSale"), value: derived.itemsPerSale.toFixed(1)},
                    {key: "clients", label: t("employeeDetails.stats.clientsServed"), value: statistics.total_clients_served ?? 0},
                    {
                        key: "cancelled", label: t("employeeDetails.stats.cancelledSales"), value: statistics.total_cancelled_sales ?? 0,
                        hint: statistics.total_free_sales ? t("employeeDetails.stats.freeSales", {count: statistics.total_free_sales}) : undefined,
                    },
                ]}/>
            )}

            {/* Graphiques : activité horaire + répartition du CA */}
            {(hourlyChartData || revenueSplit) && (
                <div className="grid grid-cols-1 lg:grid-cols-3 border-b border-gray-200 dark:border-gray-700 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 dark:divide-gray-700">
                    <Section className="lg:col-span-2" icon={BarChart3} title={t("employeeDetails.hourlyPerformance")}
                             aside={salesByStatus && salesByStatus.length > 0 && (
                                 <div className="hidden sm:flex flex-wrap items-center justify-end gap-2">
                                     {salesByStatus.map(({status, count}) => {
                                         const meta = saleStatusMeta(status);
                                         return (
                                             <span key={status} className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                 <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}/>{meta.label} · {count}
                                             </span>
                                         );
                                     })}
                                 </div>
                             )}>
                        <div className="p-4 sm:p-6">
                            {hourlyChartData ? (
                                <Chart chartType="ColumnChart" width="100%" height="260px" data={hourlyChartData} options={hourlyChartOptions}/>
                            ) : (
                                <EmptyChart height={260} text={t("employeeDetails.noDataForPeriod")}/>
                            )}
                        </div>
                    </Section>
                    <Section icon={PieChart} title={t("employeeDetails.revenueDistribution")}>
                        <div className="p-4 sm:p-6">
                            {revenueSplit ? (
                                <Chart chartType="PieChart" width="100%" height="260px" data={revenueSplit} options={chartOptions}/>
                            ) : (
                                <EmptyChart height={260} text={t("employeeDetails.noData")}/>
                            )}
                        </div>
                    </Section>
                </div>
            )}

            {/* Top produits */}
            {topProducts && topProducts.length > 0 && (
                <Section className="border-b border-gray-200 dark:border-gray-700" icon={Trophy} title={t("employeeDetails.topProducts")}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 px-4 sm:px-6 py-5">
                        {topProducts.map((p, i) => (
                            <div key={p.product_id || i}>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                    <span className="flex items-center gap-2 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200">
                                        <span className="w-5 text-xs font-semibold text-gray-400 tabular-nums">{i + 1}</span>
                                        <span className="truncate">{p.product_name || t("employeeDetails.freeSaleProduct")}</span>
                                    </span>
                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex-shrink-0 tabular-nums">
                                        {t("employeeDetails.unitsAbbr", {count: toNumber(p.quantity_sold)})} · {formatCurrency(p.total_revenue)} DH
                                    </span>
                                </div>
                                <div className="ml-7 h-1.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
                                    <div className="h-full rounded-full bg-primary"
                                         style={{width: `${topProductsMax > 0 ? Math.max(4, (toNumber(p.quantity_sold) / topProductsMax) * 100) : 0}%`}}/>
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Tous les produits vendus (liste ou par catégorie) */}
            <EmployeeSoldProducts
                employeeId={employeeId}
                dateRange={dateRange}
                startTime={startTime}
                endTime={endTime}
                isDarkMode={isDarkMode}
            />

            {/* Historique des ventes */}
            <Section className="border-b border-gray-200 dark:border-gray-700" icon={Receipt} title={t("employeeDetails.salesHistory")}>
                {sales_history && sales_history.length > 0 ? (
                    <>
                        {/* Desktop table */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>{t("employeeDetails.columns.date")}</th>
                                    <th className={thClass}>{t("employeeDetails.columns.saleId")}</th>
                                    <th className={`${thClass} text-right`}>{t("employeeDetails.columns.items")}</th>
                                    <th className={`${thClass} text-right`}>{t("employeeDetails.columns.amountTtc")}</th>
                                    <th className={`${thClass} text-right`}>{t("employeeDetails.columns.discount")}</th>
                                    <th className={thClass}>{t("employeeDetails.columns.payment")}</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sales_history.map((sale) => (
                                    <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{formatDateTime(sale.createdAt)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-500 dark:text-gray-400">#{sale.id.slice(0, 8)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right tabular-nums">{saleCountLabel(sale)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right tabular-nums">{formatCurrency(sale.total)} DH</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right tabular-nums text-red-600 dark:text-red-400">
                                            {sale.total_remise > 0 ? `-${formatCurrency(sale.total_remise)} DH` : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                            <span className="inline-flex items-center gap-2"><CreditCard className="w-4 h-4 text-primary"/>{paymentLabel(sale)}</span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile rows */}
                        <div className={`sm:hidden ${mobileListClass}`}>
                            {sales_history.map((sale) => (
                                <div key={sale.id} className={mobileRowClass}>
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-sm font-medium">{formatDateTime(sale.createdAt)}</span>
                                        <span className="text-sm font-semibold tabular-nums">{formatCurrency(sale.total)} DH</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        <span className="truncate">#{sale.id.slice(0, 8)} · {paymentLabel(sale)} · {t("employeeDetails.itemsAbbr", {count: saleCountLabel(sale)})}</span>
                                        {sale.total_remise > 0 && <span className="text-red-600 dark:text-red-400 flex-shrink-0">-{formatCurrency(sale.total_remise)} DH</span>}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {pagination && (
                            <div className="border-t border-gray-200 dark:border-gray-700">
                                <Pagination
                                    page={pagination.current_page}
                                    totalPages={pagination.total_pages}
                                    onPage={setCurrentPage}
                                    summary={t("employeeDetails.pageOf", {
                                        current: pagination.current_page,
                                        total: pagination.total_pages,
                                        count: pagination.total_items,
                                    })}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <ListEmpty icon={Receipt} title={t("employeeDetails.noSalesRecorded")} text={t("employeeDetails.forThisPeriod")}/>
                )}
            </Section>

            {showPrimeModal && (
                <PrimeModal
                    employeeName={employee.name}
                    totalHT={statistics?.total_revenue_ht}
                    totalTTC={statistics?.total_revenue}
                    onClose={() => setShowPrimeModal(false)}
                    isDarkMode={isDarkMode}
                />
            )}
        </div>
    );
};

export default React.memo(EmployeeDetails);
